import { useEffect, useSyncExternalStore } from 'react';
import { Music, VolumeX } from 'lucide-react';
import { DEFAULT_MUSIC_VOLUME, type Mood, type MusicState } from '@gmp/shared-ui';
import type { RoomStage } from '@gmp/shared-types/browser';
import { store } from '../net/storage';
import { sound } from './prefs';
import { ACTION_GAMES } from '../screens/GameCard';

/** This TV's own music settings (per device, in localStorage). The host's room setting can switch it off. */
export interface TvMusicPrefs {
  muted: boolean;
  volume: number;
}

const KEY = 'gmp.tvMusic';
let prefs: TvMusicPrefs = { muted: false, volume: DEFAULT_MUSIC_VOLUME, ...store.get<Partial<TvMusicPrefs>>(KEY) };
const music = sound.music;
music.setMuted(prefs.muted);
music.setVolume(prefs.volume);

export function setTvMusic(patch: Partial<TvMusicPrefs>) {
  prefs = { ...prefs, ...patch };
  store.set(KEY, prefs);
  music.setMuted(prefs.muted);
  music.setVolume(prefs.volume);
}

/** Mood for each stage: chill in the lobby, upbeat in a game (punchier in the 3D action games), a celebration at the results. */
export function moodFor(stage: RoomStage, gameId?: string): Mood {
  if (stage === 'IN_GAME') return gameId && ACTION_GAMES.has(gameId) ? 'action' : 'game';
  return stage === 'FINAL_RESULTS' ? 'final' : 'lobby';
}

let snapshot = { state: music.state as MusicState, mood: music.mood, muted: music.muted, volume: music.volume };
function read() {
  const next = { state: music.state, mood: music.mood, muted: music.muted, volume: music.volume };
  if (next.state !== snapshot.state || next.mood !== snapshot.mood || next.muted !== snapshot.muted || next.volume !== snapshot.volume) snapshot = next;
  return snapshot;
}

export function useTvMusic() {
  return useSyncExternalStore((fn) => music.subscribe(fn), read);
}

/**
 * Drives the TV's music from the room: on while the host's "TV music" setting is on, mood from the stage.
 * Only the audience screen calls this, so phones never play music. Leaving the screen stops it.
 */
export function useTvMusicDriver(stage: RoomStage, allowed: boolean, gameId?: string) {
  useEffect(() => {
    music.setMood(moodFor(stage, gameId));
  }, [stage, gameId]);
  useEffect(() => {
    music.setEnabled(allowed);
  }, [allowed]);
  useEffect(() => () => music.setEnabled(false), []);
}

/** Invisible state probe for tests (audio can't be heard in CI): data-state is off, muted, waiting or playing. */
export function TvMusicProbe({ allowed }: { allowed: boolean }) {
  const m = useTvMusic();
  return <span className="sr-only" data-testid="tv-music" data-state={m.state} data-mood={m.mood} data-allowed={allowed ? '1' : '0'} />;
}

/** Header controls: music mute and volume. Hidden when the host has TV music off. */
export function TvMusicControls({ compact }: { compact?: boolean }) {
  const m = useTvMusic();
  if (m.state === 'off') return null;
  const off = m.muted || m.volume <= 0;
  const size = compact ? 16 : 18;
  return (
    <div className="flex items-center gap-1.5 rounded-xl bg-ink/70 ring-1 ring-white/15 px-1 shrink-0" data-testid="tv-music-controls">
      <button
        className="btn btn-sm btn-secondary"
        style={compact ? { minHeight: 0, padding: 7 } : undefined}
        aria-label={off ? 'Play music' : 'Mute music'}
        aria-pressed={!off}
        title={m.state === 'waiting' ? 'Tap to let this screen play sound' : undefined}
        data-testid="tv-music-toggle"
        onClick={() => {
          sound.unlock();
          if (off && m.volume <= 0) setTvMusic({ muted: false, volume: DEFAULT_MUSIC_VOLUME });
          else setTvMusic({ muted: !m.muted });
        }}
      >
        {off ? <VolumeX size={size} /> : <Music size={size} />}
      </button>
      <input
        type="range" min={0} max={1} step={0.05} value={m.muted ? 0 : m.volume}
        aria-label="Music volume" data-testid="tv-music-volume"
        className="accent-[var(--tv,var(--accent))]"
        style={{ width: compact ? 'clamp(56px, 5.5vw, 104px)' : 120 }}
        onChange={(e) => {
          sound.unlock();
          const v = Number(e.target.value);
          setTvMusic(v > 0 ? { volume: v, muted: false } : { volume: 0 });
        }}
      />
    </div>
  );
}
